// src/components/ImageEditor/cropImage.js
//
// Renders the crop/zoom/rotation chosen in the cropper to a JPEG blob.
//
// ImageKit serves `access-control-allow-origin: *`, so an already-uploaded image
// can be pulled into a canvas without tainting it — which is what lets us crop a
// photo AFTER it was uploaded, including one that arrived from a phone.

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    // Required or canvas.toBlob() throws a SecurityError on the tainted canvas.
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

const toRadians = (deg) => (deg * Math.PI) / 180;

/**
 * Bounding box of the image once rotated, so nothing is clipped mid-rotation.
 */
const rotatedSize = (width, height, rotation) => {
  const rad = toRadians(rotation);
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
};

/**
 * @param {string} imageSrc            source URL (ImageKit or blob:)
 * @param {Object} pixelCrop           {x,y,width,height} from react-easy-crop
 * @param {number} [rotation=0]        degrees
 * @param {Object} [opts]              { quality, maxEdge }
 * @returns {Promise<Blob>} JPEG blob of the cropped region
 */
export default async function cropImage(imageSrc, pixelCrop, rotation = 0, opts = {}) {
  const quality = opts.quality ?? 0.9;
  const maxEdge = opts.maxEdge ?? 2400;

  const image = await createImage(imageSrc);

  // Draw the rotated image onto a canvas large enough to hold it...
  const box = rotatedSize(image.width, image.height, rotation);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = box.width;
  canvas.height = box.height;

  ctx.translate(box.width / 2, box.height / 2);
  ctx.rotate(toRadians(rotation));
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // ...then lift just the selected region out of it.
  // Clamp to the canvas: a crop region that runs past the edge would otherwise
  // come back transparent and render as black in the JPEG.
  const sx = Math.max(0, Math.min(Math.round(pixelCrop.x), canvas.width - 1));
  const sy = Math.max(0, Math.min(Math.round(pixelCrop.y), canvas.height - 1));
  const sw = Math.max(1, Math.min(Math.round(pixelCrop.width), canvas.width - sx));
  const sh = Math.max(1, Math.min(Math.round(pixelCrop.height), canvas.height - sy));

  const data = ctx.getImageData(sx, sy, sw, sh);

  const out = document.createElement('canvas');
  out.width = sw;
  out.height = sh;
  out.getContext('2d').putImageData(data, 0, 0);

  // Keep the result within a sane size, matching the capture-side cap.
  const longest = Math.max(out.width, out.height);
  let final = out;
  if (longest > maxEdge) {
    const scale = maxEdge / longest;
    const scaled = document.createElement('canvas');
    scaled.width = Math.round(out.width * scale);
    scaled.height = Math.round(out.height * scale);
    scaled.getContext('2d').drawImage(out, 0, 0, scaled.width, scaled.height);
    final = scaled;
  }

  return new Promise((resolve, reject) => {
    final.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not render the cropped image'))),
      'image/jpeg',
      quality,
    );
  });
}
